import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { User, LogOut, Crown, Settings, Heart, Upload, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import AuthModal from './AuthModal';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const UserMenu = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleLogout = async () => {
    const { error } = await logout();
    if (error) {
      toast({
        title: "Error",
        description: "Failed to sign out",
        variant: "destructive"
      });
    } else {
      toast({
        title: "Signed Out",
        description: "You've been successfully signed out",
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            onClick={() => setShowAuthModal(true)}
            className="text-gray-700 hover:text-gray-900"
          >
            Sign In
          </Button>
          <Button 
            onClick={() => setShowAuthModal(true)}
            className="bg-black text-white hover:bg-gray-800"
          >
            Sign Up
          </Button>
        </div>
        
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialMode="signin"
        />
      </>
    );
  }

  const getFirstName = (name, email) => {
    if (name) return name.split(' ')[0];
    if (email) return email.split('@')[0];
    return 'User';
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name[0].toUpperCase();
  };

  const isPremium = false; // TODO: Check user's membership status

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-auto px-2 py-1 rounded-full flex items-center gap-2">
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.photoURL} alt={user.displayName || user.email} />
            <AvatarFallback>
              {getInitials(user.displayName || user.email)}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium text-gray-700 hidden sm:inline">
            {getFirstName(user.displayName, user.email)}
          </span>
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent className="w-64" align="end">
        <DropdownMenuLabel className="p-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src={user.photoURL} alt={user.displayName || user.email} />
              <AvatarFallback>
                {getInitials(user.displayName || user.email)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm leading-tight">
                {getFirstName(user.displayName, user.email)}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user.email}
              </p>
              <div className="flex items-center gap-2 mt-1">
                {isPremium ? (
                  <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                    <Crown className="h-3 w-3 mr-1" />
                    Premium
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">
                    Free Account
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </DropdownMenuLabel>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem asChild>
          <Link to="/favorites" className="flex items-center gap-2 cursor-pointer">
            <Heart className="h-4 w-4 text-[#F5A623]" />
            My Walks
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuItem asChild>
          <Link to="/billing" className="flex items-center gap-2 cursor-pointer">
            <CreditCard className="h-4 w-4" />
            Billing & Subscription
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuItem asChild>
          <Link to="/submit" className="flex items-center gap-2 cursor-pointer">
            <Upload className="h-4 w-4" />
            Submit Walk
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {!isPremium && (
          <>
            <DropdownMenuItem asChild>
              <Link to="/membership" className="flex items-center gap-2 cursor-pointer">
                <Crown className="h-4 w-4 text-[#F5A623]" />
                Upgrade to Wanderloop+
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        
        <DropdownMenuItem asChild className="flex items-center gap-2 cursor-pointer">
          <Link to="/account/settings">
            <Settings className="h-4 w-4" />
            Account Settings
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          onClick={handleLogout}
          className="flex items-center gap-2 cursor-pointer text-red-600 hover:text-red-700"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;